import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import useSnackbarMessage from "../../../../utils/HandleSuccessErrorSnackbars";
import { ChatMessageData } from "@services/models/Prime/ChatHistoryModel";
import webSocketService from "@services/websocket/WebsocketServices";
import { SendMessageRequest } from "@services/models/Prime/SendMessageModel";
import { primeServices } from "@services/apis/Prime";
import MessageInputField from "./MessageInputField";
import Skeleton from "react-loading-skeleton";
import EmptyState from "@components/screens/EmptyState";
import DisplayFileSlider from "./DisplayFileSlider";
import MessageBubble from "./MessageBubble";

interface ChatMessagesPrimeBuySellProps {
  tradeId: number;
  userId: number;
}

const ChatMessagesPrimeBuySell: React.FC<ChatMessagesPrimeBuySellProps> = ({
  tradeId,
  userId,
}) => {
  const [message, setMessage] = useState<string>("");
  const [displayFile, setDisplayFile] = useState<ChatMessageData | undefined>(
    undefined
  );

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [shouldStopFetching, setShouldStopFetching] = useState(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setMessage(e.target.value);
  };

  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const { handleError } = useSnackbarMessage();
  const [lastScrolledMessage, setLastScrolledMessage] = useState<
    ChatMessageData | undefined
  >(undefined);
  const MESSAGES_PER_PAGE = 10;

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    });
  };

  const handleP2PChatMessage = (data: ChatMessageData) => {
    if (data.tradeId === tradeId) {
      setMessages((prev) => {
        // Only add if the message doesn't already exist
        const isDuplicate = prev.some(
          (msg) => msg.messageId === data.messageId
        );
        if (!isDuplicate) {
          setLastScrolledMessage(data); // Set this message as the trigger for scroll
          return [...prev, data];
        }
        return prev;
      });
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.onMessage(
          "admin_p2p_ramp_chat",
          true,
          handleP2PChatMessage
        );
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };

    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected]);

  const sendMessage = async () => {
    if (message.trim() === "") return;

    const sendMessageRequest: SendMessageRequest = {
      tradeId: tradeId,
      messageType: "text",
      messageContent: message,
      isAdmin: "2312",
    };
    try {
      const response = await primeServices.sendMessage(sendMessageRequest);
      setMessages((prev) => [...prev, response.data]);
      setLastScrolledMessage(response.data);
    } catch (error: any) {
      handleError({
        message: error?.response?.data?.msg ?? "Failed to send message",
      });
    } finally {
      setMessage("");
    }
  };

  // Fetch messages
  const fetchMessages = async (currentOffset: number) => {
    if (loading || isLoadingMore || shouldStopFetching) return;

    try {
      setLoading(true);

      // Get current scroll position and height before loading new messages
      const scrollContainer = chatContainerRef.current;
      const previousScrollHeight = scrollContainer?.scrollHeight || 0;
      const previousScrollTop = scrollContainer?.scrollTop || 0;

      const response = await primeServices.getChatHistoryByTradeId({
        tradeId,
        limit: MESSAGES_PER_PAGE,
        offset: currentOffset,
        isAdmin: "2312",
      });

      const fetchedMessages = response.data.reverse();

      setMessages((prev) => [...fetchedMessages, ...prev]);
      setHasMore(response.meta.hasMore);
      setOffset(response.meta.nextOffset ?? 0);

      // Restore scroll position after new messages are loaded
      requestAnimationFrame(() => {
        if (scrollContainer) {
          const newScrollHeight = scrollContainer.scrollHeight;
          const scrollDiff = newScrollHeight - previousScrollHeight;
          scrollContainer.scrollTop = previousScrollTop + scrollDiff;
        }
      });
    } catch (error: any) {
      setFetchError(true);
      setShouldStopFetching(true);
    } finally {
      setLoading(false);
    }
  };

  // Modified intersection observer setup
  useEffect(() => {
    const options = {
      root: chatContainerRef.current,
      threshold: 0.1,
      rootMargin: "50px",
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading) {
        fetchMessages(offset);
      }
    };

    const observer = new IntersectionObserver(handleIntersect, options);

    if (loadMoreTriggerRef.current) {
      observer.observe(loadMoreTriggerRef.current);
    }

    return () => observer.disconnect();
  }, [offset, hasMore, loading, fetchError]);

  // Initial fetch effect
  useEffect(() => {
    const initialLoad = async () => {
      if (messages.length === 0 && !loading && !fetchError) {
        // Ensure no active loading or errors and no messages loaded
        setLoading(true);
        try {
          const response = await primeServices.getChatHistoryByTradeId({
            tradeId,
            limit: MESSAGES_PER_PAGE,
            offset: 0,
            isAdmin: "2312",
          });

          setMessages(response.data.reverse());
          setHasMore(response.meta.hasMore);
          setOffset(response.meta.nextOffset ?? 0);
        } catch (error: any) {
          setFetchError(true);
          setShouldStopFetching(true);
        } finally {
          setLoading(false);
        }
      }
    };

    initialLoad();

    // Cleanup on component unmount
    return () => {
      setMessages([]);
      setOffset(0);
      setHasMore(true);
      setFetchError(false);
      setShouldStopFetching(false);
    };
  }, [tradeId]); // Ensure this only runs when tradeId changes

  // Scroll to bottom only for new messages (not for pagination)
  useEffect(() => {
    const shouldScrollToBottom =
      messages.length > 0 && !loading && offset === MESSAGES_PER_PAGE; // Only for initial load or new messages

    if (shouldScrollToBottom) {
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }
      });
    }
  }, [messages, loading, offset]);

  useEffect(() => {
    if (
      lastScrolledMessage &&
      messages.length > 0 &&
      lastScrolledMessage === messages[messages.length - 1]
    ) {
      scrollToBottom();
    }
  }, [lastScrolledMessage, messages]); // Dependency on lastScrolledMessage ensures scroll only when updated

  return (
    <div className="flex flex-col h-full relative">
      <div
        className="flex flex-col h-full overflow-y-auto"
        ref={chatContainerRef}
        style={{
          height: "calc(100dvh - 400px)",
        }}
      >
        {loading && (
          <div className="space-y-[16px]">
            {[...Array(2)].map((_, index) => (
              <div key={index} className="flex flex-col space-y-[16px]">
                <Skeleton width="80%" height={20} />
                <Skeleton width="60%" height={20} />
              </div>
            ))}
          </div>
        )}
        <div ref={loadMoreTriggerRef} className="h-4" />
        {(messages.length === 0 || fetchError) && !loading ? (
          <EmptyState
            text={"No messages yet."}
            subText={
              "Start the conversation now or wait for the other party to send a message!"
            }
            className="flex flex-col items-center justify-center h-[66vh]"
          />
        ) : (
          <>
            <div className="gap-4 pb-20">
              {messages.map((message) => (
                <MessageBubble
                  key={message.messageId}
                  chatMessageData={message}
                  userId={userId}
                  onClick={
                    message.messageType === "video" ||
                    message.messageType === "image"
                      ? () => setDisplayFile(message)
                      : undefined
                  }
                />
              ))}
            </div>
          </>
        )}

        <div className="absolute bottom-0 left-0 right-0 py-4 bg-bg-page-body">
          <MessageInputField
            value={message}
            onChange={handleInputChange}
            onSendClick={message.trim() !== "" ? sendMessage : undefined}
          />
        </div>

        <DisplayFileSlider
          isOpen={displayFile !== undefined}
          onClose={() => {
            setDisplayFile(undefined);
          }}
          chatMessageData={displayFile!}
        />
      </div>
    </div>
  );
};

export default ChatMessagesPrimeBuySell;
